import type { MailStatus, SentMailSummary } from '@myrewards/contracts';
import type { StatusTone } from '../ui';

// Settings › System administration › Sent mail: every email the app sent or tried to send, with where it actually went.

export const MAIL_STATUS: Record<MailStatus, { label: string; tone: StatusTone; filter: string }> =
  {
    queued: { label: 'Queued', tone: 'waiting', filter: 'Queued' },
    sent: { label: 'Sent', tone: 'done', filter: 'Sent' },
    // Outside production the app keeps a copy instead of delivering it.
    captured: { label: 'Captured, not delivered', tone: 'info', filter: 'Captured' },
    skipped: { label: 'Skipped', tone: 'neutral', filter: 'Skipped' },
    failed: { label: 'Failed', tone: 'problem', filter: 'Failed' },
  };

export const STATUS_ORDER: readonly MailStatus[] = [
  'sent',
  'captured',
  'queued',
  'failed',
  'skipped',
];

export function statusFromParam(value: string | null): MailStatus | null {
  return STATUS_ORDER.find((s) => s === value) ?? null;
}

export function listHref(status: MailStatus | null): string {
  return status === null
    ? '/settings/system/sent-mail'
    : `/settings/system/sent-mail?status=${status}`;
}

/** "Executives (group) · executives@example.test", "Jordan Ellis · jordan.ellis@…". */
export function recipientWords(mail: Pick<SentMailSummary, 'recipient'>): {
  name: string;
  address: string;
} {
  const r = mail.recipient;
  return {
    name: r.kind === 'group' ? `${r.name} (group)` : r.name,
    address: r.address ?? 'No address on file',
  };
}

const TRANSPORT: Record<NonNullable<SentMailSummary['transport']>, string> = {
  graph: 'Microsoft Graph',
  capture: 'Kept as a copy (not delivered)',
  off: 'Email turned off',
};

export function transportWords(transport: SentMailSummary['transport']): string {
  return transport === null ? 'Not sent yet' : TRANSPORT[transport];
}

/**
 * What became of the message, for the words around its text (RUI-012, APP-110): only a sent email
 * was sent, and nobody saw one that wasn't delivered. The status and its pill stay as they are.
 */
const MESSAGE_WORDS: Record<
  MailStatus,
  { html: string; text: string; note: string; content: string }
> = {
  sent: { html: 'as it was sent', text: 'sent', note: '', content: 'what was sent' },
  captured: {
    html: 'as it was kept instead of delivered',
    text: 'kept',
    note: '',
    content: 'what was kept',
  },
  queued: { html: 'as it will be sent', text: 'that goes', note: '', content: 'what will be sent' },
  failed: {
    html: 'as it was written',
    text: 'written',
    note: ' It wasn’t delivered.',
    content: 'what was written; it wasn’t delivered',
  },
  skipped: {
    html: 'as it was written',
    text: 'written',
    note: ' It wasn’t sent.',
    content: 'what was written; it wasn’t sent',
  },
};

/** The help on the email's two cards, true to what became of it. */
export function messageHelp(status: MailStatus): { html: string; text: string } {
  const w = MESSAGE_WORDS[status];
  return {
    html: `The HTML version ${w.html}, in a locked-down frame: no scripts, no links that open, nothing loaded from outside.${w.note}`,
    text: `The plain-text version ${w.text} with the HTML, for mail apps that don’t show HTML.${w.note}`,
  };
}

/** Why there's no .eml to download, or null when there is one. */
export function emlMissing(detail: {
  status: MailStatus;
  captureExpired: boolean;
  links: { eml: string | null };
}): string | null {
  if (detail.captureExpired) return 'Message body expired after 30 days';
  if (detail.links.eml === null)
    return `No .eml copy: only captured messages keep one. The text and HTML above are ${MESSAGE_WORDS[detail.status].content}.`;
  return null;
}
